import React, { useState, useCallback, useEffect } from "react"
import styled from "styled-components"
import { Form, Field, withFormik } from "formik"
import * as Yup from "yup"
import queryString from "query-string"
import { withRouter } from "react-router-dom"
import Empty from "./Empty"
import CheckoutFooter from "../CheckoutFooter"
import { OrderField } from "../../common/Fields"
import Products from "./Products"
import { API_PUBLIC } from "../../api"
import Loader from "../../common/Loader"

const StyledOrderForm = styled.div`
  width: 90%;
  margin: 0 auto;
  max-width: 1000px;
  padding: 30px 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  .business-name {
    text-align: center;
    text-transform: uppercase;
    font-size: 30px;
    font-weight: 900;
    max-width: 670px;
  }
  .product-search {
    display: block;
    max-width: 670px;
    width: 100%;
  }

  @media screen and (max-width: 800px) {
    width: 100%;
    padding-top: 0;
    > .product-search {
      width: 90%;
      margin: 0 auto 0px;
    }
  }
`

function OrderForm({ location, values, setFieldValue }) {
  const { products, quantity } = values
  const uuid = queryString.parse(location.search, { ignoreQueryPrefix: true })
    .uid

  function setQuantity(value, index) {
    const q = [...quantity]
    q[index] = value
    setFieldValue("quantity", q)
  }

  const [options, setOptions] = useState([])
  const [allProducts, setAllProducts] = useState([])

  useEffect(
    () => {
      const p = [...products]
      const pIds = p.map(p => p.id)
      const o = allProducts.filter(p => !pIds.includes(p.id))
      setOptions(o)
    },
    [products, allProducts]
  )

  function addProduct(value) {
    const p = [...products]
    const q = [...quantity]

    p.push(value)
    q.push(1)
    setFieldValue("products", p)
    setFieldValue("quantity", q)
  }

  function removeProduct(id) {
    const p = [...products]
    const q = [...quantity]

    const product = p.find(p => p.id === id)
    const index = p.indexOf(product)
    p.splice(index, 1)
    q.splice(index, 1)
    setFieldValue("products", p)
    setFieldValue("quantity", q)
  }

  function clearProducts() {
    setFieldValue("products", [])
    setFieldValue("quantity", [])
  }

  const [storeData, setStoreData] = useState(null)

  const getStoreData = useCallback(
    async () => {
      try {
        if (!uuid) return
        API_PUBLIC.defaults.headers.common["account-uuid"] = uuid
        const [storeInfo, productInfo] = await Promise.all([
          API_PUBLIC.get(`/checkout`),
          API_PUBLIC.get(`/orders/search_product`)
        ])
        const productData = productInfo.data.product_output.map(product => ({
          id: product.variant_id,
          value: product.variant_id,
          snapshot_id: product.snapshot_id,
          label: `${product.product}
        ${product.variant === "default_option" ? "" : `(${product.variant})`}
      `,
          name: product.product,
          variant: product.variant,
          image: product.image,
          price: product.price,
          inventory_quantity: product.quantity,
          weight: product.weight,
          weight_unit: product.weight_unit,
          is_limited: product.is_limited
        }))

        setStoreData(storeInfo.data)
        setAllProducts(productData)
        setOptions(productData)
        setFieldValue("info", storeInfo.data)
      } catch (error) {
        console.log(error)
      }
    },
    [setFieldValue, uuid]
  )

  useEffect(
    () => {
      getStoreData()
    },
    [getStoreData]
  )

  const [subTotal, setSubtotal] = useState(0)
  useEffect(
    () => {
      let computed = 0
      products.forEach((p, i) => {
        computed += parseFloat(p.price) * parseInt(quantity[i])
      })
      setSubtotal(computed)
    },
    [products, quantity]
  )

  const helpers = {
    setQuantity: setQuantity,
    removeProduct: removeProduct,
    clearProducts: clearProducts
  }

  function onKeyDown(keyEvent) {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault()
    }
  }

  return (
    <>
      {(storeData && (
        <Form onKeyDown={onKeyDown}>
          <StyledOrderForm className="OrderForm">
            <h1 className="business-name">{storeData.account.businessname}</h1>
            <span className="product-search">
              <Field
                name="product"
                component={OrderField}
                value={null}
                options={options}
                placeholder="Search for a product name"
                onChange={addProduct}
              />
            </span>

            {products.length > 0 ? (
              <Products
                products={products}
                quantity={quantity}
                helpers={helpers}
                subTotal={subTotal}
                currency={storeData.account.currency}
              />
            ) : (
              <Empty />
            )}
          </StyledOrderForm>
          <div
            style={{
              position: "absolute",
              bottom: 0,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center"
            }}
          >
            <CheckoutFooter businessName={storeData.account.businessname} />
          </div>
        </Form>
      )) || <Loader />}
    </>
  )
}

export default withFormik({
  enableReinitialize: true,
  mapPropsToValues: () => ({
    products: [],
    quantity: [],
    info: {}
  }),
  validationSchema: Yup.object().shape({
    products: Yup.array().required(),
    quantity: Yup.array().required()
  }),
  handleSubmit: (values, { props }) => {
    props.submitOrderForm(values)
  }
})(withRouter(OrderForm))
